import { useId, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../api/errors';
import { notificationsApi } from '../../api/notifications-api';

const DAY = 24 * 60 * 60 * 1000;

function dateInput(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * Leaves one device out of the summary until a date, with a reason
 * that also goes on its site's status log.
 */
export function SnoozeDialog({
  itemId,
  siteName,
  maxDays,
  onClose,
}: {
  itemId: string;
  siteName: string;
  maxDays: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const formId = useId();
  const today = Date.now();
  const [until, setUntil] = useState(dateInput(today + Math.min(30, maxDays) * DAY));
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const snooze = useMutation({
    mutationFn: () =>
      notificationsApi.createSnooze({ itemId, until: `${until}T23:59:59Z`, reason: reason.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      onClose();
    },
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : 'The snooze could not be saved. Try again.'),
  });

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!reason.trim()) {
      setError('Say why, e.g. “Harvested, returning in November”.');
      return;
    }
    setError(null);
    snooze.mutate();
  }

  return (
    <div className="drawer-overlay centered-overlay" role="presentation" onClick={onClose}>
      <form
        className="confirmation-dialog form-surface"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${formId}-title`}
        onClick={(event) => event.stopPropagation()}
        onSubmit={submit}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
        }}
        noValidate
      >
        <div className="drawer-heading">
          <h2 id={`${formId}-title`}>Snooze {itemId}</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close snooze dialog"
          >
            ×
          </button>
        </div>
        <p className="drawer-version-note">
          Left out of the summary until the date. The reason is added to {siteName}’s status log.
        </p>
        {error ? (
          <p role="alert" className="form-error">
            {error}
          </p>
        ) : null}
        <label>
          Until
          <input
            type="date"
            value={until}
            min={dateInput(today + DAY)}
            max={dateInput(today + maxDays * DAY)}
            onChange={(event) => setUntil(event.target.value)}
            required
          />
        </label>
        <label>
          Reason
          <textarea
            rows={3}
            maxLength={500}
            value={reason}
            placeholder="Harvested, returning in November"
            onChange={(event) => setReason(event.target.value)}
            autoFocus
          />
        </label>
        <div className="dialog-actions">
          <button type="button" onClick={onClose} disabled={snooze.isPending}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={snooze.isPending}>
            {snooze.isPending ? 'Saving…' : 'Snooze'}
          </button>
        </div>
      </form>
    </div>
  );
}
